import { Link } from "react-router-dom";
import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { FAQS_TRAINING } from "@/data/content";

const LMS_CAPABILITIES = [
  {
    title: "Structured course delivery",
    body:
      "Create and manage structured learning pathways for drivers, instructors, managers, operational staff, safety-critical teams and wider rail workforces.",
  },
  {
    title: "CPD and professional development",
    body:
      "Support continuous professional development with auditable learning records, course completion evidence, reflective learning and structured progression.",
  },
  {
    title: "Knowledge checks and assessments",
    body:
      "Build quizzes, checks of understanding, scenario-based learning and post-course assessments to evidence learner understanding and training effectiveness.",
  },
  {
    title: "Learner progress tracking",
    body:
      "Monitor individual and team progress, completion status, engagement, assessment outcomes and training gaps through clear management visibility.",
  },
  {
    title: "Evidence-led assurance",
    body:
      "Capture training evidence, assessment outcomes, completion records and learner activity in a format suitable for internal assurance and compliance review.",
  },
  {
    title: "Role-based learning pathways",
    body:
      "Assign learning based on role, competence requirement, operational exposure, development need, team, depot, location or organisational structure.",
  },
];

const RAILWAY_DIFFERENTIATORS = [
  {
    title: "Built around railway operational reality",
    body:
      "TRAC-Learn is designed for safety-critical learning environments where training needs to be practical, traceable and aligned to real operational risk.",
  },
  {
    title: "Designed for competence and assurance teams",
    body:
      "The platform supports training managers, competence teams and operational leaders who need visibility of learning, development and compliance evidence.",
  },
  {
    title: "Supports blended delivery",
    body:
      "Use TRAC-Learn for online learning, classroom preparation, refresher training, briefings, CPD, workshops and post-training evidence capture.",
  },
  {
    title: "Scalable across organisations",
    body:
      "Suitable for small specialist teams, training providers, heritage railways and larger operators looking for structured learning governance.",
  },
];

const GOVERNANCE_POINTS = [
  "Training records that support audit, assurance and internal governance.",
  "Clear learner evidence for CPD, refresher training and structured development.",
  "Management visibility across courses, teams, completion data and knowledge checks.",
  "Support for consistent training delivery across locations, instructors and learner groups.",
  "Designed to complement competence management, risk management and operational assurance processes.",
];

const TRAC_LEARN_EXAMPLES = [
  {
    title: "VR Traction example",
    body: "An immersive VR traction course showing how operational cab training can be delivered safely and engagingly.",
    to: "/vr-traction",
  },
  {
    title: "VR PTS example",
    body: "A Personal Track Safety VR scenario designed to build situational awareness before live track exposure.",
    to: "/vr-pts",
  },
  {
    title: "eLearning example",
    body: "A structured online module example for railway learning, assessment and CPD evidence capture.",
    to: "/elearning",
  },
];

export default function TRACLearn() {
  return (
    <>
      <SEO
        title="TRAC-Learn | Railway Learning Management System | UKTDA"
        description="TRAC-Learn is a railway-focused Learning Management System supporting courses, CPD, assessments, learner progress, training evidence and operational assurance."
        path="/trac-learn"
      />

      <PageHero
        eyebrow="TRAC-Learn"
        title="An industry-leading LMS for railway training, CPD and safety-critical learning."
        intro="TRAC-Learn is a modern Learning Management System designed for the railway sector. It helps organisations deliver structured training, evidence learner competence, manage CPD, assess knowledge and maintain clear assurance visibility across safety-critical teams."
      >
        <div className="flex items-center gap-4">
          <div className="flex h-28 w-28 md:h-36 md:w-36 items-center justify-center rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
            <img
              src="/TRAC-Learn.svg"
              alt="TRAC-Learn logo"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
      </PageHero>

      <section className="py-20 md:py-28 bg-white" data-testid="trac-learn-positioning">
        <div className="container-rail">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">Railway LMS</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl text-navy leading-tight">
                Learning management built for organisations where training evidence matters.
              </h2>

              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Railway training is not just about delivering content. It is about proving that the
                right people have received the right learning, understood the right risks, completed
                the right checks and generated the right evidence.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                TRAC-Learn gives railway organisations a structured platform for managing learning
                pathways, CPD, operational briefings, refresher training, assessments and development
                programmes. It is designed to support training quality, learner readiness, competence
                evidence and organisational assurance.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-8 shadow-sm">
              <p className="label-eyebrow text-safety">What it supports</p>

              <h3 className="mt-3 font-display font-bold text-2xl text-navy">
                A single LMS for training, CPD, knowledge checks and evidence.
              </h3>

              <div className="mt-6 space-y-4">
                {[
                  "Course delivery",
                  "CPD records",
                  "Learner progress",
                  "Knowledge checks",
                  "Training evidence",
                  "Assurance visibility",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="h-2.5 w-2.5 rounded-full bg-safety" />
                    <p className="font-semibold text-slate-800">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="trac-learn-capabilities">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Platform capabilities</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Everything needed to deliver, evidence and manage railway learning.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              TRAC-Learn brings core LMS functionality together with a railway-focused approach to
              training governance, learner development, operational risk and compliance evidence.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {LMS_CAPABILITIES.map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-navy text-white" data-testid="trac-learn-railway-specific">
        <div className="container-rail">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">Built for rail</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
                Not a generic LMS adapted for rail. A rail-focused LMS built around assurance.
              </h2>

              <p className="mt-5 text-white/75 leading-relaxed">
                Generic learning platforms often focus on content hosting. TRAC-Learn goes further
                by supporting the evidence, structure and oversight needed in safety-critical railway
                environments.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              {RAILWAY_DIFFERENTIATORS.map((item) => (
                <article
                  key={item.title}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <h3 className="font-display font-bold text-xl text-white">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-white/70 leading-relaxed">
                    {item.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="trac-learn-assurance">
        <div className="container-rail">
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <p className="label-eyebrow text-safety">Training assurance</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Turn learning activity into usable assurance evidence.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                TRAC-Learn helps organisations move beyond attendance lists and disconnected
                training records. It supports a clearer evidence chain between learning needs,
                course delivery, learner progress, assessment outcomes and compliance visibility.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                This makes it easier to demonstrate that learning has been delivered consistently,
                understood appropriately and recorded in a way that supports operational governance.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-8">
              <h3 className="font-display font-bold text-2xl text-navy">
                Governance-focused LMS design
              </h3>

              <ul className="mt-6 space-y-4">
                {GOVERNANCE_POINTS.map((point) => (
                  <li key={point} className="flex gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-safety" />
                    <span className="text-slate-700 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="trac-learn-enterprise">
        <div className="container-rail">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <div>
                <p className="label-eyebrow text-safety">Enterprise-ready</p>

                <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                  Designed for serious training environments, not informal content libraries.
                </h2>

                <p className="mt-5 text-slate-600 leading-relaxed">
                  TRAC-Learn is positioned for organisations that need structure, reliability,
                  learner visibility and defensible evidence. It can support internal training
                  teams, external training providers and organisations managing safety-critical
                  workforce development.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  "Role-based access",
                  "Learner records",
                  "Assessment evidence",
                  "CPD tracking",
                  "Course governance",
                  "Audit support",
                  "Training visibility",
                  "Scalable delivery",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <p className="font-semibold text-navy">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="label-eyebrow text-safety">Information security</p>

              <h3 className="mt-3 font-display font-bold text-2xl text-navy">
                Built with ISO 27001-aligned thinking.
              </h3>

              <p className="mt-3 text-slate-600 leading-relaxed">
                For enterprise railway customers, learning records and competence evidence are
                organisational assets. TRAC-Learn is designed with security, access control,
                auditability, supplier assurance, policy governance and data protection principles
                in mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="trac-learn-use-cases">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Use cases</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              One platform for railway learning, development and assurance.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              TRAC-Learn can be used across a wide range of railway training and professional
              development requirements.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Driver development",
                body: "Structured learning for trainee drivers, qualified drivers, instructors and driver managers.",
              },
              {
                title: "Operational briefings",
                body: "Deliver controlled briefings, knowledge updates, refresher content and evidence of understanding.",
              },
              {
                title: "CPD programmes",
                body: "Manage professional development pathways with completion records and learner reflection.",
              },
              {
                title: "Training providers",
                body: "Support course delivery, learner management, assessment records and client-facing evidence.",
              },
            ].map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="trac-learn-example-links">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Example training formats</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Explore sample VR and online training that TRAC-Learn can support.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              These examples show how immersive traction, track safety and eLearning course formats can be delivered within the TRAC ecosystem.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {TRAC_LEARN_EXAMPLES.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-safety/60"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-safety">
                  Example
                </p>
                <h3 className="mt-4 font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {item.body}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-safety">
                  View example
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQSection
        items={FAQS_TRAINING}
        title="TRAC-Learn FAQs"
        intro="The questions most often asked about TRAC-Learn, railway LMS delivery, training evidence, CPD and organisational assurance."
        schemaId="faq-trac-learn"
      />

      <CTASection
        eyebrow="TRAC-Learn"
        title="Discuss an LMS built around railway training, CPD and assurance."
        intro="Tell us what your organisation needs to deliver, evidence or improve. We’ll help identify how TRAC-Learn can support your training, learner development and compliance visibility."
        primary={{
          label: "Contact us",
          to: "/contact",
          testId: "trac-learn-cta-contact",
        }}
        secondary={{
          label: "Explore TRAC-CMS",
          to: "/trac-cms",
          testId: "trac-learn-cta-trac-cms",
        }}
      />
    </>
  );
}